<template>
  <div v-if="entity">
    <h1>Add card</h1>
    <v-btn @click="navigate"> <v-icon>chevron_left</v-icon>Cards </v-btn>
    <EntityWorking :entity="entity" />
    <v-alert :value="true" color="info">
      <p>
        Please enter below the details for your credit or debit card. Any
        details entered will be encrypted using TLS and transmitted securely.
      </p>
      <p>
        <span>Credit card surcharge:&#160;</span>
        <span
          >If your designated card is a credit card you will be charged a 2.5%
          handling fee on any transactions. If your card is a debit card you
          will not be charged a handling fee.</span
        >
      </p>
    </v-alert>
    <div>
      <v-img
        :src="require(`@/assets/payment/mastercardMaestroVisa.jpg`)"
        alt="Card types logo"
        height="50"
        contain
      />
    </div>
    <v-form ref="cardForm" v-model="valid" lazy-validation>
      <v-select
        label="Card type"
        v-model="card.cardType"
        :items="cardTypes"
        item-value="id"
        item-label="text"
        outline
      />
      <v-text-field
        label="Card number"
        v-model="card.number"
        :rules="lengthRules45.concat(valueRules)"
        :counter="45"
        :error-messages="cardErrors"
        outline
      >
        <template slot="append-outer">
          <a @click="dialogCardNumber = true">What is the card number?</a>
        </template>
      </v-text-field>
      <v-text-field
        label="CVC number"
        v-model="card.cvc"
        :rules="valueRules.concat(lengthRules3)"
        :counter="3"
        outline
      >
        <template slot="append-outer">
          <a @click="dialogCvcNumber = true">What is the CVC number?</a>
        </template>
      </v-text-field>
      <v-text-field
        label="Expiry date"
        v-model="card.expire"
        v-mask="'##/##'"
        :rules="valueRules"
        outline
      >
        <template slot="append-outer">
          <a @click="dialogExpiry = true">What is the expiry date?</a>
        </template>
      </v-text-field>
      <v-text-field
        label="Cardholder name"
        v-model="card.name"
        :rules="lengthRules45.concat(valueRules)"
        :counter="45"
        outline
      >
        <template slot="append-outer">
          <a @click="dialogCardholderName = true"
            >What is the cardholder name?</a
          >
        </template>
      </v-text-field>
      <v-textarea
        label="Cardholder address"
        v-model="card.address"
        :rules="valueRules"
        outline
      />
      <v-btn @click="addCard"> <v-icon>save</v-icon>Save </v-btn>
    </v-form>
    <v-dialog v-model="dialogCardNumber" width="300">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title
          >What is the card number?</v-card-title
        >
        <v-card-text>
          <p>
            The card number is the long number written across the front of the
            card.
          </p>
          <v-img
            :src="require(`@/assets/payment/cardnumber.jpg`)"
            alt="Front of card"
            height="150"
            contain
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogCvcNumber" width="300">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title
          >What is the CVC number?</v-card-title
        >
        <v-card-text>
          <p>
            The CVC number is located on the back of the card. The last 3
            digits.
          </p>
          <v-img
            :src="require(`@/assets/payment/cvc.jpg`)"
            alt="Back of card"
            height="150"
            contain
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogCardholderName" width="300">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title
          >What is the cardholder name?</v-card-title
        >
        <v-card-text>
          <p>
            The cardholder name is usually written on the front of the card
            below the card number
          </p>
          <v-img
            :src="require(`@/assets/payment/cardnumber.jpg`)"
            alt="Front of card"
            height="150"
            contain
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogExpiry" width="300">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title
          >What is the expiry date?</v-card-title
        >
        <v-card-text>
          <p>
            The expiry date is normally found under the card number in the form
            MONTH/YEAR. Two digits for the month and last two digits of the
            year. For example 08/20 for August 2020.
          </p>
          <v-img
            :src="require(`@/assets/payment/cardnumber.jpg`)"
            alt="Front of card"
            height="150"
            contain
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EntityWorking from "@/components/link/EntityWorking";
import nbmlaw from "@/api/nbmlaw";
export default {
  name: "Card",
  metaInfo() {
    return {
      title: "Card",
      titleTemplate: "%s - Payments - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true,
      },
    };
  },
  components: { EntityWorking },
  data() {
    return {
      valid: false,
      entity: null,
      card: { cardType: "DEBIT" },
      cardTypes: [
        { id: "CREDIT", text: "Credit" },
        { id: "DEBIT", text: "Debit" },
      ],
      dialogCardNumber: false,
      dialogCvcNumber: false,
      dialogCardholderName: false,
      dialogExpiry: false,
      valueRules: [(v) => !!v || "Value is required"],
      lengthRules45: [
        (v) =>
          !v || (v && v.length <= 60) || "This must be less than 60 characters",
      ],
      lengthRules3: [
        (v) =>
          !v || (v && v.length == 3) || "This must be exactly 3 characters",
      ],
      cardErrors: [],
    };
  },
  methods: {
    fetchEntity() {
      nbmlaw
        .get(`/cors/admin/directory/entities/${this.$route.params.id}`)
        .then((response) => {
          // console.log(response.data);
          this.entity = response.data;
        })
        .catch(() => {});
    },
    async addCard() {
      if (this.$refs.cardForm.validate()) {
        await this.validateCard();
        if (this.cardErrors.length == 0) {
          this.saveCard();
        }
      }
    },
    async validateCard() {
      const response = await nbmlaw
        .get(`/cors/admin/payment/cards/validate`, {
          params: { value: this.card.number },
        })
        .catch(() => {});
      if (response.data == false) {
        this.cardErrors = ["Card number not valid"];
      } else if (response.data == true) {
        this.cardErrors = [];
      }
    },
    saveCard() {
      nbmlaw
        .post(
          `/cors/admin/directory/entities/${this.$route.params.id}/cards`,
          this.card
        )
        .then(() => {
          // console.log(response.data);
          this.navigate();
        })
        .catch(() => {});
    },
    navigate() {
      this.$router.push({
        name: "customer",
        params: { id: this.$route.params.id },
      });
    },
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        this.fetchEntity();
      },
    },
  },
};
</script>